import { type Criterion, type CriterionValue, type OperatorType } from '@spike/automation-model';

const DAYS_AGO_WORDS: Partial<Record<OperatorType, string>> = {
    days_ago_gt: 'more than',
    days_ago_lt: 'less than',
    days_ago_eq: 'exactly'
};

const NUMERIC_SYMBOLS: Partial<Record<OperatorType, string>> = {
    gt: '>',
    lt: '<',
    eq: '='
};

const NEGATED: OperatorType[] = ['is_not', 'not_in'];

// Fields whose operators read better as possession than as membership.
export const FIELD_OPERATOR_LABELS: Record<string, Partial<Record<OperatorType, string>>> = {
    dog_requirements: { in: 'Has', not_in: 'Does not have' },
    cat_requirements: { in: 'Has', not_in: 'Does not have' }
};

// Two-option booleans no longer offer "is not", so a saved one reads as the other option.
export const effectiveBooleanValue = (criterion: CriterionValue, definition: Criterion): unknown => {
    if (criterion.operator !== 'is_not') return criterion.value;

    return definition.options?.find(o => o.value !== criterion.value)?.value ?? criterion.value;
};

export type CriterionIndex = Record<string, Criterion>;

export const buildCriterionChipLabel = (criterion: CriterionValue, byField: CriterionIndex): string => {
    const definition = byField[criterion.field];

    if (!definition) return criterion.field;

    const prefix = definition.label;
    const negated = NEGATED.includes(criterion.operator) ? 'not ' : '';

    if (definition.fieldType === 'boolean') {
        const value = effectiveBooleanValue(criterion, definition);
        const option = definition.options?.find(o => o.value === value);
        return `${prefix}: ${option?.label ?? value}`;
    }

    if (definition.fieldType === 'multi_enum') {
        const values = Array.isArray(criterion.value) ? criterion.value : [];
        if (values.length === 0) return prefix;

        const labels = values.map(v => definition.options?.find(o => o.value === v)?.label ?? v);
        const verb = FIELD_OPERATOR_LABELS[criterion.field]?.[criterion.operator];
        const lead = verb ? `${verb.toLowerCase()} ` : negated;
        return `${prefix}: ${lead}${labels.join(', ')}`;
    }

    if (definition.fieldType === 'date_relative') {
        const word = DAYS_AGO_WORDS[criterion.operator] ?? criterion.operator;
        return `${prefix}: ${word} ${criterion.value} ${definition.unit ?? 'days'} ago`;
    }

    if (definition.fieldType === 'numeric') {
        const symbol = NUMERIC_SYMBOLS[criterion.operator] ?? criterion.operator;
        const unit = definition.unit ?? '';
        return `${prefix}: ${symbol} ${unit}${criterion.value}`;
    }

    return prefix;
};

export const buildCriterionChipLabels = (criteria: CriterionValue[], byField: CriterionIndex): string[] =>
    criteria.map(criterion => buildCriterionChipLabel(criterion, byField));
